"use client"

import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '@joinomu/shared'

export interface MetricCategory {
  key: string
  name: string
  unit: string
  enabled: boolean
}

interface MetricPreferencesState {
  preferences: MetricCategory[]
  isLoading: boolean
  updatePreference: (key: string, enabled: boolean) => Promise<void>
  loadPreferences: () => Promise<void>
}

const MetricPreferencesContext = createContext<MetricPreferencesState | undefined>(undefined)

const defaultMetricCategories: Omit<MetricCategory, 'enabled'>[] = [
  { key: 'weight', name: 'Weight', unit: 'lbs' },
  { key: 'steps', name: 'Steps', unit: 'steps' },
  { key: 'sleep', name: 'Sleep', unit: 'hours' },
  { key: 'calories', name: 'Calories', unit: 'kcal' },
  { key: 'protein', name: 'Protein', unit: 'grams' },
  { key: 'sugar', name: 'Sugar', unit: 'grams' },
  { key: 'water', name: 'Water', unit: 'fl oz' },
  { key: 'heart-rate', name: 'Average Heart Rate', unit: 'bpm' },
]

export function MetricPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<MetricCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadPreferences = async () => {
    try {
      setIsLoading(true)
      
      // Safety check: ensure supabase client is available
      if (!supabase) {
        console.warn('Supabase client not available, using default preferences')
        setPreferences(defaultMetricCategories.map(cat => ({ ...cat, enabled: true })))
        setIsLoading(false)
        return
      }
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setPreferences(defaultMetricCategories.map(cat => ({ ...cat, enabled: true })))
        setIsLoading(false)
        return
      }

      const { data: existingPrefs, error } = await supabase
        .from('metric_preferences')
        .select('*')
        .eq('user_id', user.id)

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error loading metric preferences:', error)
        setPreferences(defaultMetricCategories.map(cat => ({ ...cat, enabled: true })))
        setIsLoading(false)
        return
      }

      // Create preferences array with defaults and user overrides
      const userPrefsMap = new Map((existingPrefs || []).map(pref => [pref.metric_key, pref.enabled]))
      
      const finalPreferences = defaultMetricCategories.map(cat => ({
        ...cat,
        enabled: userPrefsMap.has(cat.key) ? userPrefsMap.get(cat.key)! : true // Default all to enabled
      }))

      setPreferences(finalPreferences)
    } catch (error) {
      console.error('Error in loadPreferences:', error)
      setPreferences(defaultMetricCategories.map(cat => ({ ...cat, enabled: true })))
    } finally {
      setIsLoading(false)
    }
  }

  const updatePreference = async (key: string, enabled: boolean) => {
    try {
      // Safety check: ensure supabase client is available
      if (!supabase) {
        console.warn('Supabase client not available, cannot update preferences')
        return
      }
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Update local state immediately for better UX
      setPreferences(prev => 
        prev.map(pref => 
          pref.key === key ? { ...pref, enabled } : pref
        )
      )

      // Upsert preference in database
      const { error } = await supabase
        .from('metric_preferences')
        .upsert({
          user_id: user.id,
          metric_key: key,
          enabled: enabled,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,metric_key'
        })

      if (error) {
        console.error('Error updating metric preference:', error)
        // Revert local state on error
        setPreferences(prev => 
          prev.map(pref => 
            pref.key === key ? { ...pref, enabled: !enabled } : pref
          )
        )
      }
    } catch (error) {
      console.error('Error in updatePreference:', error)
      // Revert local state on error
      setPreferences(prev => 
        prev.map(pref => 
          pref.key === key ? { ...pref, enabled: !enabled } : pref
        )
      )
    }
  }

  useEffect(() => {
    loadPreferences()
  }, [])

  const value: MetricPreferencesState = {
    preferences,
    isLoading,
    updatePreference,
    loadPreferences,
  }

  return (
    <MetricPreferencesContext.Provider value={value}>
      {children}
    </MetricPreferencesContext.Provider>
  )
}

export function useMetricPreferences() {
  const context = useContext(MetricPreferencesContext)
  if (context === undefined) {
    // Return a safe default when provider is not available
    console.warn('useMetricPreferences called outside of MetricPreferencesProvider, using defaults')
    return {
      preferences: defaultMetricCategories.map(cat => ({ ...cat, enabled: true })), // Default all enabled
      isLoading: false,
      updatePreference: async () => {
        console.warn('Cannot update preferences without MetricPreferencesProvider')
      },
      loadPreferences: async () => {
        console.warn('Cannot load preferences without MetricPreferencesProvider')
      }
    }
  }
  return context
}